import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import SubscribeForm from "@/components/feature/SubscribeForm";
import useSeo, { SITE_NAME, SITE_ORIGIN } from "@/hooks/useSeo";
import { getRumorById, rumors, type Rumor } from "@/mocks/rumors";
import { formatDateLabel, formatHeat } from "@/utils/format";

function ReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed left-0 top-0 z-[60] h-0.5 w-full bg-transparent">
      <div className="h-full bg-primary-600 transition-[width] duration-150" style={{ width: `${progress}%` }} />
    </div>
  );
}

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const rumor = getRumorById(id);
  const [copied, setCopied] = useState(false);

  const related = useMemo<Rumor[]>(() => {
    if (!rumor) return [];
    const sameCat = rumors.filter((r) => r.categorySlug === rumor.categorySlug && r.id !== rumor.id);
    const others = rumors.filter((r) => r.categorySlug !== rumor.categorySlug && r.id !== rumor.id);
    return [...sameCat, ...others].slice(0, 3);
  }, [rumor]);

  const headings = useMemo(
    () => (rumor ? rumor.blocks.filter((b) => b.type === "h2").map((b) => b.text) : []),
    [rumor]
  );

  useSeo({
    title: rumor ? `${rumor.title} - 热点内幕独家爆料` : "爆料不存在 - 热点内幕",
    description: rumor ? rumor.excerpt : "该爆料内容可能已被移除或链接有误。",
    keywords: rumor ? `热点内幕,${rumor.tags.join(",")},独家爆料` : "热点内幕,独家爆料",
    path: `/article/${id}`,
    type: "article",
    jsonLd: rumor
      ? [
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: rumor.title,
            description: rumor.excerpt,
            image: [rumor.cover],
            datePublished: rumor.publishedAt.replace(" ", "T"),
            dateModified: rumor.publishedAt.replace(" ", "T"),
            articleSection: rumor.categoryName,
            keywords: rumor.tags.join(","),
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: SITE_NAME },
            publisher: { "@type": "Organization", name: SITE_NAME },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_ORIGIN}/article/${rumor.id}` },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
              { "@type": "ListItem", position: 2, name: "爆料中心", item: `${SITE_ORIGIN}/rumors` },
              { "@type": "ListItem", position: 3, name: rumor.categoryName, item: `${SITE_ORIGIN}/rumors?cat=${rumor.categorySlug}` },
              { "@type": "ListItem", position: 4, name: rumor.title, item: `${SITE_ORIGIN}/article/${rumor.id}` },
            ],
          },
        ]
      : undefined,
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!rumor) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="container-editorial flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">这条爆料找不到了</h1>
          <p className="mt-2 text-sm text-foreground-600">链接可能已失效，或者内容已被合并到其他条目。</p>
          <Link
            to="/rumors"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line text-base" />
            返回爆料中心
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const ranking = [...rumors].sort((a, b) => b.heat - a.heat).slice(0, 5);

  return (
    <div className="min-h-screen bg-background-50">
      <ReadingProgress />
      <SiteHeader />
      <main>
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="container-editorial py-6 md:py-8">
            <Breadcrumbs
              items={[
                { label: "爆料中心", to: "/rumors" },
                { label: rumor.categoryName, to: `/rumors?cat=${rumor.categorySlug}` },
                { label: rumor.title },
              ]}
            />
          </div>
        </section>

        <section className="container-editorial py-8 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-10">
            <article className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={`/rumors?cat=${rumor.categorySlug}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                >
                  <i className="ri-price-tag-3-line text-xs" />
                  {rumor.categoryName}
                </Link>
                <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-fire-line text-primary-500" />
                  热度 {formatHeat(rumor.heat)}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-[34px]">
                {rumor.title}
              </h1>

              <p className="mt-4 rounded-lg border-l-4 border-primary-500 bg-background-100/70 p-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {rumor.excerpt}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200/80 py-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-star-line text-base text-primary-600" />
                  {rumor.insider}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-base" />
                  {formatDateLabel(rumor.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-base" />
                  本文持续跟进更新
                </span>
              </div>

              <div className="mt-6 h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
                <img
                  src={rumor.cover}
                  alt={`${rumor.title} - 热点内幕独家爆料配图`}
                  title={`${rumor.title} - 热点内幕`}
                  className="h-full w-full object-top object-cover"
                />
              </div>

              <div className="prose-neimu mt-8 max-w-none">
                {rumor.blocks.map((block, index) => {
                  if (block.type === "h2") {
                    return (
                      <h2 key={`${block.text}-${index}`} id={`sec-${index}`}>
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "quote") {
                    return (
                      <blockquote key={`${block.text}-${index}`}>
                        <i className="ri-chat-quote-line mr-2 text-accent-600" />
                        {block.text}
                      </blockquote>
                    );
                  }
                  return <p key={`${block.text}-${index}`}>{block.text}</p>;
                })}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">标签：</span>
                {rumor.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/rumors?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200/80 bg-background-100/60 p-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-600">觉得有料？分享给也在等消息的人</span>
                  {copied && (
                    <span className="flex items-center gap-1 text-xs font-medium text-accent-700 animate-fade-in">
                      <i className="ri-check-line" />
                      已复制链接
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-link" />
                    复制链接
                  </button>
                  <Link
                    to="/rumors"
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-list-check-2" />
                    更多爆料
                  </Link>
                </div>
              </div>

              <div className="mt-10">
                <h2 className="mb-5 font-heading text-xl font-black text-foreground-950">相关爆料推荐</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {related.map((item, index) => (
                    <ArticleCard key={item.id} rumor={item} variant="grid" index={index} />
                  ))}
                </div>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="sticky top-28 space-y-5">
                {headings.length > 0 && (
                  <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                    <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                      <i className="ri-list-unordered text-primary-600" />
                      本页提纲
                    </h2>
                    <ul className="mt-3 space-y-2.5">
                      {headings.map((text) => (
                        <li key={text}>
                          <a
                            href={`#sec-${rumor.blocks.findIndex((b) => b.type === "h2" && b.text === text)}`}
                            className="flex items-start gap-2 text-sm leading-snug text-foreground-600 transition-colors hover:text-primary-700"
                          >
                            <i className="ri-subtract-line mt-0.5 text-primary-400" />
                            {text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-bar-chart-2-line text-primary-600" />
                    热度排行
                  </h2>
                  <ol className="mt-4 space-y-3">
                    {ranking.map((item, index) => (
                      <li key={item.id} className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                            index < 3 ? "bg-primary-600 text-background-50" : "bg-background-300 text-foreground-700"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <Link
                          to={`/article/${item.id}`}
                          className="text-sm leading-snug text-foreground-800 transition-colors hover:text-primary-700 line-clamp-2"
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-notification-3-line text-accent-600" />
                    订阅爆料提醒
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    同类事件的后续进展，我们会在第一时间通知你。
                  </p>
                  <div className="mt-4">
                    <SubscribeForm />
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}